import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang, fmtPrice, fmtMoney, SHIPPING } from "@/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Leaf, Loader2, ArrowLeft, Puzzle, Share2 } from "lucide-react";
import { shareProduct } from "@/components/game/GameOffer";
import { Pic } from "@/components/Pic";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyShip = { name: "", address: "", city: "", phone: "" };
const GAMES = { tablero_pame3d: { cards: "/juegos/tablero/", play: "/tablero" }, hamburguesa_armar: { cards: "/juegos/hamburguesa/", play: "/hamburguesa" }, pizza_enroscable: { cards: "/juegos/pizza/", play: "/pizza" } };

export const ProductDialog = ({ product, open, onClose }) => {
  const { t, lang, currency } = useLang();
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("info");
  const [ship, setShip] = useState(emptyShip);
  if (!product) return null;
  const name = lang === "es" ? product.name_es : product.name_en;
  const desc = lang === "es" ? product.desc_es : product.desc_en;
  const price = fmtPrice(product, currency);
  const unit = currency === "usd" ? product.price_usd : product.price_cop;
  const shipCost = SHIPPING[currency];

  const close = () => { setStep("info"); setShip(emptyShip); setQty(1); onClose(); };

  const pay = async () => {
    if (!ship.name || !ship.address || !ship.city || !ship.phone) {
      toast.error(t.catalog.shipRequired);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        product_id: product.id, quantity: qty, currency,
        origin_url: window.location.origin, shipping: ship,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Error al iniciar el pago");
      setLoading(false);
    }
  };

  const shipField = (k, label) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-[#132A1D]">{label}<span className="text-[#C86A4B]"> *</span></Label>
      <Input data-testid={`ship-${k}`} value={ship[k]} onChange={(e) => setShip({ ...ship, [k]: e.target.value })}
        className="bg-white border-black/10 h-10 rounded-lg" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="product-dialog" className="max-w-3xl bg-[#F7F5F0] border-black/10 p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="grid md:grid-cols-2">
          <div className="aspect-square overflow-hidden bg-[#EFECE6] hidden md:flex items-center justify-center">
            <Pic src={product.image} alt={`${name} impreso en 3D con material reciclado`} loading="eager" className="w-full h-full object-contain" />
          </div>
          <div className="p-6 md:p-8 flex flex-col">
            {step === "info" ? (
              <>
                <div className="aspect-[16/9] overflow-hidden rounded-xl bg-[#EFECE6] mb-4 md:hidden">
                  <Pic src={product.image} alt={`${name} impreso en 3D con material reciclado`} loading="eager" className="w-full h-full object-contain" />
                </div>
                <DialogTitle className="font-serif-d text-3xl font-semibold text-[#132A1D] tracking-tight">{name}</DialogTitle>
                <DialogDescription className="text-sm text-[#5C5C5C] mt-3 leading-relaxed">{desc}</DialogDescription>
                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Leaf size={15} className="text-[#2E5A3B]" />
                    <span className="font-semibold text-[#2E5A3B]">{t.catalog.material}:</span> {product.material}
                  </div>
                  <div>
                    <span className="font-semibold text-[#2E5A3B]">{t.catalog.colors}:</span>{" "}
                    {product.colors?.map((c) => (
                      <span key={c} className="inline-block bg-[#EFECE6] rounded-full px-3 py-0.5 text-xs mr-1.5 mt-1">{c}</span>
                    ))}
                  </div>
                  <button data-testid="product-share-btn" onClick={() => shareProduct({ title: name, text: t.offer.productShareText.replace("{name}", name).replace("{price}", price || ""), url: `https://pame3d.com${GAMES[product.slug] ? GAMES[product.slug].play : `/catalogo/${product.slug.replace(/_/g, "-")}`}` })}
                    className="inline-flex items-center gap-2 font-semibold text-[#25D366] hover:text-[#2E5A3B] transition-colors duration-200 underline underline-offset-4 mr-4">
                    <Share2 size={15} /> {t.offer.productShare}
                  </button>
                  {GAMES[product.slug] && (
                    <a href={GAMES[product.slug].cards} data-testid="game-cards-link"
                      className="inline-flex items-center gap-2 font-semibold text-[#2E5A3B] hover:text-[#C86A4B] transition-colors duration-200 underline underline-offset-4">
                      <Puzzle size={15} /> {t.catalog.gameCards}
                    </a>
                  )}
                  {GAMES[product.slug] && (
                    <Link to={GAMES[product.slug].play} data-testid="game-play-link"
                      className="inline-flex items-center gap-2 font-semibold text-[#C86A4B] hover:text-[#2E5A3B] transition-colors duration-200 underline underline-offset-4">
                      <Puzzle size={15} /> {t.catalog.play}
                    </Link>
                  )}
                </div>
                <div className="mt-auto pt-6">
                  {product.purchasable && price ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p data-testid="product-price" className="font-serif-d text-3xl font-semibold text-[#132A1D]">{price}</p>
                          <p data-testid="product-shipping-note" className="text-xs text-[#5C5C5C] mt-0.5">{t.catalog.shippingNote}: {fmtMoney(shipCost, currency)}</p>
                        </div>
                        <div className="flex items-center gap-3 border border-black/10 rounded-full px-2 py-1">
                          <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-1 hover:text-[#C86A4B]"><Minus size={14} /></button>
                          <span data-testid="qty-value" className="text-sm font-bold w-5 text-center">{qty}</span>
                          <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="p-1 hover:text-[#C86A4B]"><Plus size={14} /></button>
                        </div>
                      </div>
                      <Button data-testid="buy-now-btn" onClick={() => setStep("shipping")}
                        className="w-full rounded-full bg-[#C86A4B] hover:bg-[#2E5A3B] text-white h-12 text-base font-semibold transition-colors duration-300">
                        {t.catalog.buy}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="font-serif-d italic text-2xl text-[#2E5A3B] mb-4">{t.catalog.free}</p>
                      <Button data-testid="social-contact-btn" asChild
                        className="w-full rounded-full bg-[#2E5A3B] hover:bg-[#C86A4B] text-white h-12 text-base font-semibold transition-colors duration-300">
                        <a href="https://wa.me/573106846758" target="_blank" rel="noopener noreferrer">{t.catalog.contact}</a>
                      </Button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <button data-testid="ship-back-btn" onClick={() => setStep("info")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5C5C5C] hover:text-[#C86A4B] mb-3 transition-colors duration-200">
                  <ArrowLeft size={13} /> {t.catalog.shipBack}
                </button>
                <DialogTitle className="font-serif-d text-2xl font-semibold text-[#132A1D] tracking-tight">{t.catalog.shipTitle}</DialogTitle>
                <DialogDescription className="text-sm text-[#5C5C5C] mt-1">{name} × {qty}</DialogDescription>
                <div data-testid="order-summary" className="mt-4 bg-white rounded-xl border border-black/10 p-4 text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-[#5C5C5C]">{t.catalog.subtotal}</span><span data-testid="summary-subtotal" className="font-semibold">{fmtMoney(unit * qty, currency)}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C5C5C]">{t.catalog.shipping}</span><span data-testid="summary-shipping" className="font-semibold">{fmtMoney(shipCost, currency)}</span></div>
                  <div className="flex justify-between border-t border-black/10 pt-2 mt-2"><span className="font-semibold text-[#132A1D]">{t.catalog.total}</span><span data-testid="summary-total" className="font-serif-d text-xl font-semibold text-[#132A1D]">{fmtMoney(unit * qty + shipCost, currency)}</span></div>
                </div>
                <div className="mt-5 space-y-4">
                  {shipField("name", t.catalog.shipName)}
                  {shipField("address", t.catalog.shipAddress)}
                  <div className="grid grid-cols-2 gap-4">
                    {shipField("city", t.catalog.shipCity)}
                    {shipField("phone", t.catalog.shipPhone)}
                  </div>
                </div>
                <div className="mt-auto pt-6">
                  <Button data-testid="ship-continue-btn" onClick={pay} disabled={loading}
                    className="w-full rounded-full bg-[#C86A4B] hover:bg-[#2E5A3B] text-white h-12 text-base font-semibold transition-colors duration-300">
                    {loading ? <><Loader2 className="animate-spin mr-2" size={18} />{t.catalog.buying}</> : t.catalog.shipContinue}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
